import React, { useState } from "react";
import { db } from "../lib/instant";
import { Play, Youtube } from "lucide-react";

function getThumbnail(videoId) {
  return `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`;
}

export default function YouTubeSection({ theme }) {
  const primaryColor   = theme?.primaryColor   || "#6b2118";
  const accentColor    = theme?.accentColor    || "#c9a84c";
  const secondaryColor = theme?.secondaryColor || "#2e7d32";
  const [playing, setPlaying] = useState(null);

  const { data } = db.useQuery({ videos: { $: { where: { active: true } } } });
  const videos = [...(data?.videos || [])].sort((a, b) => (a.order || 0) - (b.order || 0));

  if (videos.length === 0) return null;

  return (
    <section style={{ padding: "80px 5vw", background: "#fff" }}>
      <div style={{ maxWidth: 1200, margin: "0 auto" }}>
        {/* Header */}
        <div style={{ textAlign: "center", marginBottom: 48 }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "#fff0f0", padding: "6px 16px", borderRadius: 40, marginBottom: 14 }}>
            <Youtube size={16} color="#ff0000" />
            <span style={{ fontSize: "0.72rem", letterSpacing: 3, textTransform: "uppercase", color: "#ff0000", fontWeight: 700 }}>Watch & Learn</span>
          </div>
          <h2 style={{ fontFamily: "Georgia, serif", fontSize: "clamp(1.6rem, 3vw, 2.4rem)", color: "#2c1810", margin: 0 }}>
            See ACHU Health Mix <span style={{ color: secondaryColor }}>in Action</span>
          </h2>
        </div>

        {/* Single video — full width */}
        {videos.length === 1 && (
          <div style={{ maxWidth: 720, margin: "0 auto" }}>
            <VideoCard video={videos[0]} playing={playing} setPlaying={setPlaying} primaryColor={primaryColor} accentColor={accentColor} large />
          </div>
        )}

        {/* Two videos — side by side */}
        {videos.length === 2 && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
            {videos.map(v => <VideoCard key={v.id} video={v} playing={playing} setPlaying={setPlaying} primaryColor={primaryColor} accentColor={accentColor} />)}
          </div>
        )}

        {/* 3+ videos — first large, rest small grid */}
        {videos.length >= 3 && (
          <div>
            <div style={{ marginBottom: 24 }}>
              <VideoCard video={videos[0]} playing={playing} setPlaying={setPlaying} primaryColor={primaryColor} accentColor={accentColor} large />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: 20 }}>
              {videos.slice(1).map(v => <VideoCard key={v.id} video={v} playing={playing} setPlaying={setPlaying} primaryColor={primaryColor} accentColor={accentColor} />)}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function VideoCard({ video, playing, setPlaying, primaryColor, accentColor, large }) {
  const isPlaying = playing === video.id;

  return (
    <div style={{ borderRadius: 16, overflow: "hidden", boxShadow: "0 4px 24px rgba(0,0,0,0.1)", background: "#000" }}>
      {isPlaying ? (
        /* Embedded player */
        <div style={{ position: "relative", paddingBottom: "56.25%", height: 0 }}>
          <iframe
            src={`https://www.youtube.com/embed/${video.videoId}?autoplay=1&rel=0`}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: "none" }}
          />
        </div>
      ) : (
        /* Thumbnail with play button */
        <div
          onClick={() => setPlaying(video.id)}
          style={{ position: "relative", paddingBottom: "56.25%", height: 0, cursor: "pointer", overflow: "hidden" }}
        >
          <img
            src={`https://img.youtube.com/vi/${video.videoId}/hqdefault.jpg`}
            alt={video.title}
            style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", transition: "transform 0.3s" }}
            onMouseEnter={e => e.target.style.transform = "scale(1.05)"}
            onMouseLeave={e => e.target.style.transform = "scale(1)"}
          />
          {/* Dark overlay */}
          <div style={{ position: "absolute", inset: 0, background: "rgba(0,0,0,0.25)", transition: "background 0.2s" }}
            onMouseEnter={e => e.target.style.background = "rgba(0,0,0,0.15)"}
            onMouseLeave={e => e.target.style.background = "rgba(0,0,0,0.25)"}
          />
          {/* Play button */}
          <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div style={{
              width: large ? 72 : 56, height: large ? 72 : 56,
              borderRadius: "50%", background: "rgba(255,0,0,0.92)",
              display: "flex", alignItems: "center", justifyContent: "center",
              boxShadow: "0 4px 24px rgba(255,0,0,0.4)",
              transition: "transform 0.2s, box-shadow 0.2s",
            }}>
              <div style={{
                width: 0, height: 0,
                borderTop: `${large ? 14 : 11}px solid transparent`,
                borderBottom: `${large ? 14 : 11}px solid transparent`,
                borderLeft: `${large ? 24 : 18}px solid #fff`,
                marginLeft: large ? 5 : 4,
              }} />
            </div>
          </div>
          {/* YouTube logo */}
          <div style={{ position: "absolute", bottom: 12, right: 12 }}>
            <Youtube size={20} color="#fff" style={{ filter: "drop-shadow(0 1px 3px rgba(0,0,0,0.5))" }} />
          </div>
        </div>
      )}

      {/* Title & desc */}
      <div style={{ padding: large ? "16px 20px 18px" : "12px 16px 14px", background: "#fff" }}>
        <div style={{ fontWeight: 700, color: "#2c1810", fontSize: large ? "1rem" : "0.88rem", lineHeight: 1.3, marginBottom: video.description ? 5 : 0 }}>
          {video.title}
        </div>
        {video.description && (
          <div style={{ fontSize: "0.78rem", color: "#888", lineHeight: 1.5 }}>{video.description}</div>
        )}
      </div>
    </div>
  );
}
